import React, { useRef } from 'react';
import Drawer from '@mui/material/Drawer';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Button,
  Typography
} from '@mui/joy';
import ImageEditor from '../ImageEditor';
import { GeneratedPolicyModal, TextCropModal, FullscreenImageModal } from './modals';

/**
 * Right-side drawer for image/PDF reference upload and field testing
 */
const ImageReferencePanel = ({
  open,
  onClose,
  testPage,
  setTestingPage,
  clearTestingPage,
  selectedIndex,
  runTestField,
  isTestLoading,
  isGeneratedPolicyModal,
  closeGeneratedPolicyModal,
  policyDefinition,
  applyPolicyDefinition,
  isTextCropModal,
  closeTextCroppingModal,
  cropBox,
  openModal,
  handleModalClose,
  onFileChange
}) => {
  const fileFieldRef = useRef(null);
  const hasSelectedField = typeof selectedIndex === 'number' && selectedIndex >= 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            width: { xs: '100vw', sm: 520, md: 620 },
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            gap: 1,
          }}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Typography level="h3">Image Reference</Typography>
            <Button variant="plain" onClick={onClose}>
              Close
            </Button>
          </Stack>

          <Typography level="body-sm" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            Upload an image or PDF as a reference for data extraction.
          </Typography>

          <Card sx={{ borderRadius: 16, flex: 1, minHeight: 0, overflow: "hidden" }}>
            <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {testPage ? (
                  testPage.file?.type?.includes('pdf') ? (
                    <iframe
                      src={testPage.url}
                      title="PDF Viewer"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: '1px solid #ccc',
                        borderRadius: '8px'
                      }}
                    />
                  ) : (
                    <ImageEditor src={testPage.url} setTestingPage={setTestingPage} />
                  )
                ) : (
                  <Typography level="body-sm" sx={{ opacity: 0.8 }}>
                    No file selected yet.
                  </Typography>
                )}
              </Box>

              <Stack
                direction={testPage ? "row" : "column"}
                sx={{ gap: 1, alignItems: "center", justifyContent: "center" }}
              >
                <Button variant="outlined" onClick={() => fileFieldRef?.current?.click()}>
                  Upload PDF/Image
                </Button>
                <Button color="danger" variant="plain" onClick={clearTestingPage} disabled={!testPage?.url}>
                  Clear
                </Button>
                <input
                  hidden
                  type="file"
                  accept="application/pdf, image/*"
                  ref={fileFieldRef}
                  onChange={onFileChange}
                  data-testid="image-reference-file-input"
                />
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
            <Button variant="plain" onClick={onClose}>
              Cancel
            </Button>
            <Button
              data-testid="drawer-test-button"
              onClick={() => runTestField(selectedIndex)}
              loading={isTestLoading}
              loadingPosition="start"
              disabled={!testPage?.file || !hasSelectedField}
            >
              {isTestLoading ? "Testing" : "Test"}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <GeneratedPolicyModal
        open={isGeneratedPolicyModal}
        onClose={closeGeneratedPolicyModal}
        policyDefinition={policyDefinition}
        onApply={applyPolicyDefinition}
      />

      <TextCropModal
        open={isTextCropModal}
        onClose={closeTextCroppingModal}
        cropBox={cropBox}
        imageUrl={testPage?.url}
      />

      <FullscreenImageModal
        open={openModal}
        onClose={handleModalClose}
        testPage={testPage}
      />
    </>
  );
};

export default ImageReferencePanel;
